<script>/**
 * @exports MTableSortableMixin
 * @mixin MTableSortableMixin
 * @emits 'sort'
 *
 * contain logic about table sorting
 *
 * @typedef {{name: string, direction: any}} Sort
 */

  import LibEventBus from 'utils/libEventBus';

  export default {
    name: 'MTableSortableMixin',
    props: {
      sortable: Boolean,
      disableSortingPredicate: {
        type: Function,
        default: () => false
      },
      multiSortable: {
        type: Boolean,
        default: false
      },
      initialSorting: {
        type: Array,
        default: Array
      }
    },
    data () {
      return {

        /**
         * array with current {@type Sort} objects
         * @type {Sort[]}
         */
        sorting: this.initialSorting,

        /**
         * ordered states of sorting directions
         * `none` state means no sort
         * when direction changes, we get new value from this list cyclically
         * @type {String[]}
         */
        sortDirectionStates: ['none', 'asc', 'desc']
      };
    },
    mounted () {
      this.registerBeforeHeaderRenderedHook(this.sortableBeforeHeaderRenderedHook.bind(this));
      this.clickHandler = this.changeSortDirection.bind(this);
      this.registerHeaderEventsListener('click', this.clickHandler);
      if (this.sortable && this.sorting.length === 0) {
        this.$nextTick(() => {
          this.sorting = this.getDefaultSorting();
        });
      }
    },
    destroyed () {
      this.unregisterHeaderEventsListener('click', this.clickHandler);
    },
    methods: {
      getDefaultOrderingForColumns (column) {
        const {firstSortDirection} = column;
        if (firstSortDirection === 'asc') {
          return ['none', 'asc', 'desc'];
        }
        return ['none', 'desc', 'asc'];
      },

      getSortDirectionMapForColumns (column) {
        const defaultOrdering = this.getDefaultOrderingForColumns(column);
        const statesMap = {};

        defaultOrdering.forEach((value, index, array) => {
          statesMap[value] = array[(index + 1) % array.length];
        });

        return statesMap;
      },

      sortableBeforeHeaderRenderedHook (column) {

        /** bind click 'event' listener on sortable columns */
        const {sortable, itemKey} = column;
        if (sortable) {
          const defaultSortState = this.sortDirectionStates[0];
          const sorting = this.sorting.find(sort => sort.name === itemKey);
          this.headerProps.sortDirection = sorting ? sorting.direction : defaultSortState;
        }
      },

      /**
       * build array of {@type Sort} objects from {@link MTableColumn} defaultSortDirection props
       * @returns {Array<Sort>}
       */
      getDefaultSorting () {
        const defaultSortState = this.sortDirectionStates[0];
        const defaultSorting = [];
        this.columns.forEach(column => {
          const {itemKey, sortable, defaultSortDirection = defaultSortState} = column;
          if (sortable && defaultSortDirection !== defaultSortState) {
            defaultSorting.push({name: itemKey, direction: defaultSortDirection});
          }
        });

        this.sortSortingByPriority(defaultSorting);
        return defaultSorting;
      },

      /**
       * sort {@param sorting} array with {@link MTableColumn} sortPriority prop
       * @param {Array<Sort>} sorting
       * @returns {Array<Sort>} - sorted {@param sorting}
       */
      sortSortingByPriority (sorting) {
        sorting.sort((lhs, rhs) => {
          const lhsColumn = this.columnsFlat.find(column => column.itemKey === lhs.name);
          const rhsColumn = this.columnsFlat.find(column => column.itemKey === rhs.name);

          const {sortPriority: lhsSortPriority = 0} = lhsColumn;
          const {sortPriority: rhsSortPriority = 0} = rhsColumn;

          return rhsSortPriority - lhsSortPriority;
        });
      },

      /**
       * @emits 'sort' event with {@link sorting} as payload
       */
      sort () {
        this.$emit('sort', this.sorting);
      },

      changeSortDirection ([event, columnName]) {
        const withShiftHold = event.shiftKey;

        if (this.disableSortingPredicate(columnName)) {
          return;
        }
        const column = this.columnsMap[columnName];

        // sort only sortable columns
        if (column && column.sortable) {
          let prevDirection;

          let existingIndex = this.sorting.findIndex(sort => sort.name === columnName);
          if (existingIndex >= 0) {
            const existingSorting = this.sorting[existingIndex];
            prevDirection = existingSorting.direction;
          }

          // if `multiSortable` is false, clear other sorts, or if SHIFT is not pressed
          if (!this.multiSortable || !withShiftHold) {
            this.sorting = [];
            existingIndex = -1;
          }

          const defaultState = this.sortDirectionStates[0];
          const sortDirectionMap = this.getSortDirectionMapForColumns(column);

          // do not set none for column that was clicked
          let newState = sortDirectionMap[prevDirection || defaultState];
          if (newState === 'none') {
            newState = sortDirectionMap[newState];
          }

          // default state is 'none'
          if (newState !== defaultState) {

            if (existingIndex >= 0) {
              this.sorting[existingIndex] = {
                name: columnName,
                direction: newState
              };
            } else {
              this.sorting = [
                ...this.sorting,
                {
                  name: columnName,
                  direction: newState
                }
              ];
            }

            // order sorting by `MTableColumn.props.sortPriority`
            this.sortSortingByPriority(this.sorting);
          }

          // emit sort event
          LibEventBus.$emit('ON_TABLE_SORT', {column: column.itemKey, multiSortable: this.multiSortable});
          this.sort();
        }
      }
    }
  };
</script>
